/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';

import ContentFrame from '../../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareItem, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../../components/ScrollAware';
import { GroupThemedSecondaryNav } from '../../../../components/SecondaryNav';
import { AppHeaderScrollableGroup } from '../../../../components/appNav/AppHeaderScrollable';
import ContentWrapper from '../../../../components/ContentWrapper';

import GroupInfoActive from './GroupInfoActive';
import GroupInfoInactive from './GroupInfoInactive';
import LeaveGroup from './LeaveGroup.redux';

import RouteNames from '../../../RouteNames';
import { navigate } from '../../../redux/NavigationActions';
import { GroupSurveys } from '../../../../views/groupSurvey/GroupSurveys';
import { services } from '../../../../../apiServices/services';
import { ContentCategory } from '../../../../../apiServices/contentService';
import { GroupResources } from './GroupResources';
import GroupMetadata from '../../../../../models/GroupMetadata';
import GroupBanner from './GroupBanner';

const GroupDetailsNav = ({ active, hasSurveys, hasResources }) => {
  const secondaryNav = [
    { value: 'info', labelData: { id: 'list.profileSecondaryNav.info.name' }, isActive: active === 'info' },
  ];

  if (hasSurveys) {
    secondaryNav.push(
      { value: 'surveys', labelData: { id: 'group.secondaryNav.survey.name' }, isActive: active === 'surveys' },
    );
  }

  if (hasResources) {
    secondaryNav.push(
      { value: 'resources', labelData: { id: 'group.secondaryNav.resources.name' }, isActive: active === 'resources' },
    );
  }

  return (
    <GroupThemedSecondaryNav
      selectSecondaryNav={navigate}
      secondaryNav={secondaryNav}
    />
  );
};
GroupDetailsNav.propTypes = {
  active: PropTypes.string,
  hasSurveys: PropTypes.bool,
  hasResources: PropTypes.bool,
};

class GroupDetails extends React.Component {
  state = {
    surveys: [],
    resources: [],
  };

  UNSAFE_componentWillMount() {
    this.props.setActiveGroup(this.props.groupId);
  }

  async componentDidMount() {
    const resources = await services.content.getContents(this.props.profileId, this.props.groupId, ContentCategory.Resource);
    const surveys = await services.surveys.getSurveys(this.props.profileId);

    this.setState({
      resources,
      surveys: surveys.filter(s => s.group_id === this.props.groupId),
    });
  }

  render() {
    const group = new GroupMetadata(this.props.group);

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            colorThemeName="group"
            goBack={this.props.isLeaving ? null : this.props.onGoBack}
            cancel={this.props.isLeaving ? this.props.onCancel : null}
            color="white"
            isNav={true}
          />

          <ContentFrame>
            <Switch>
              <Route exact path={RouteNames.leaveGroup()}>
                <ScrollAwareScroller key={this.props.isLeaving}>
                  <AppHeaderScrollableGroup
                    group={this.props.group}
                    groupId={this.props.groupId}
                  />

                  <LeaveGroup {...this.props} />
                </ScrollAwareScroller>
              </Route>

              <Route path={RouteNames.groupInfo()}>
                <ScrollAwareScroller>
                  <AppHeaderScrollableGroup
                    group={this.props.group}
                    groupId={this.props.groupId}
                  />

                  <ScrollAwareItem isSticky hasDropShadow>
                    <GroupDetailsNav
                      active="info"
                      hasSurveys={!!this.state.surveys.length}
                      hasResources={!!this.state.resources.length}
                    />
                  </ScrollAwareItem>

                  <GroupBanner imageBanner={group.imagesBanner.get()} />

                  {
                    this.props.isActiveGroup
                    ? (
                      <ContentWrapper isFullWidth={true}>
                        <GroupInfoActive {...this.props} />
                      </ContentWrapper>
                    ) : (
                      <ContentWrapper isFullWidth={false}>
                        <GroupInfoInactive {...this.props} />
                      </ContentWrapper>
                    )
                  }
                </ScrollAwareScroller>
              </Route>

              {
                this.state.surveys.length &&
                <Route path={RouteNames.surveys()}>
                  <ScrollAwareScroller>
                    <AppHeaderScrollableGroup
                      group={this.props.group}
                      groupId={this.props.groupId}
                    />

                    <ScrollAwareItem isSticky hasDropShadow>
                      <GroupDetailsNav
                        active="surveys"
                        hasSurveys={!!this.state.surveys.length}
                        hasResources={!!this.state.resources.length}
                      />
                    </ScrollAwareItem>

                    <ContentWrapper>
                      <GroupSurveys groupId={this.props.groupId} />
                    </ContentWrapper>
                  </ScrollAwareScroller>
                </Route>
              }

              {
                this.state.resources.length &&
                <Route path={RouteNames.groupResources()}>
                  <ScrollAwareScroller>
                    <AppHeaderScrollableGroup
                      group={this.props.group}
                      groupId={this.props.groupId}
                    />

                    <ScrollAwareItem isSticky hasDropShadow>
                      <GroupDetailsNav
                        active="resources"
                        hasSurveys={!!this.state.surveys.length}
                        hasResources={!!this.state.resources.length}
                      />
                    </ScrollAwareItem>

                    <ContentWrapper>
                      <GroupResources
                        resources={this.state.resources}
                        group={this.props.group}
                      />
                    </ContentWrapper>
                  </ScrollAwareScroller>
                </Route>
              }
            </Switch>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}
GroupDetails.propTypes = {
  onSelectSecondaryNav: PropTypes.func,
  onGoBack: PropTypes.func,
  onCancel: PropTypes.func,
  groupId: PropTypes.number,
  group: PropTypes.object,
  isLeaving: PropTypes.bool,
  setActiveGroup: PropTypes.func,
  surveys: PropTypes.any,
  profileId: PropTypes.number,
  preferredLanguageCode: PropTypes.string,
  isActiveGroup: PropTypes.bool,
};

export default GroupDetails;
